import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";

const CourseCoordination = () => {
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [courseName, setCourseName] = useState("");
  const [courseCode, setCourseCode] = useState("");
  const [sectionsCoordinated, setSectionsCoordinated] = useState("");
  const [facultyMembersCoordinated, setFacultyMembersCoordinated] =
    useState("");
  const [trimesterSession, setTrimesterSession] = useState("");

  // id of the record currently being edited (null = adding a new record)
  const [editingId, setEditingId] = useState(null);

  const [courseCoordinationList, setCourseCoordinationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const token = localStorage.getItem("token");

  const authHeaders = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const clearForm = () => {
    setStartDate("");
    setEndDate("");
    setCourseName("");
    setCourseCode("");
    setSectionsCoordinated("");
    setFacultyMembersCoordinated("");
    setTrimesterSession("");
    setEditingId(null);
  };

  const refreshList = async () => {
    const refreshedData = await axios.get(
      `${BACKEND_URL}/api/course_coordination`,
      authHeaders
    );

    setCourseCoordinationList(
      Array.isArray(refreshedData.data) ? refreshedData.data : []
    );
  };

  // =========================================================
  // Load previously saved Course Coordination records
  // =========================================================
  useEffect(() => {
    const fetchCourseCoordination = async () => {
      try {
        if (!token) {
          navigate("/login");
          return;
        }

        const response = await axios.get(
          `${BACKEND_URL}/api/course_coordination`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setCourseCoordinationList(
          Array.isArray(response.data) ? response.data : []
        );
      } catch (error) {
        console.error("Error loading Course Coordination:", error);

        toast.error(
          error.response?.data?.message ||
            "Failed to load Course Coordination data."
        );
      } finally {
        setFetching(false);
      }
    };

    fetchCourseCoordination();
  }, [token, navigate]);

  // =========================================================
  // Convert a stored date to YYYY-MM-DD for <input type="date">
  // =========================================================
  const toInputDate = (value) => {
    if (!value) return "";

    // ISO strings such as 2024-01-15 or 2024-01-15T00:00:00.000Z
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }

    const d = new Date(value);

    if (isNaN(d.getTime())) return "";

    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${d.getFullYear()}-${month}-${day}`;
  };

  // =========================================================
  // Start editing a record (fills the form)
  // =========================================================
  const handleEdit = (item) => {
    setEditingId(item.id);
    setStartDate(toInputDate(item.start_date));
    setEndDate(toInputDate(item.end_date));
    setCourseName(item.course_name || "");
    setCourseCode(item.course_code || "");
    setSectionsCoordinated(
      item.sections_coordinated !== null &&
        item.sections_coordinated !== undefined
        ? String(item.sections_coordinated)
        : ""
    );
    setFacultyMembersCoordinated(
      item.faculty_members_coordinated !== null &&
        item.faculty_members_coordinated !== undefined
        ? String(item.faculty_members_coordinated)
        : ""
    );
    setTrimesterSession(item.trimester_session || "");

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // =========================================================
  // Save (add new) or Update (edit) Course Coordination
  // =========================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!startDate) {
      toast.warning("Please select the start date.");
      return;
    }

    if (!endDate) {
      toast.warning("Please select the end date.");
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      toast.warning("End date cannot be earlier than start date.");
      return;
    }

    if (!courseName.trim()) {
      toast.warning("Please enter the course name.");
      return;
    }

    if (!courseCode.trim()) {
      toast.warning("Please enter the course code.");
      return;
    }

    if (sectionsCoordinated === "" || Number(sectionsCoordinated) < 0) {
      toast.warning("Please enter a valid number of sections coordinated.");
      return;
    }

    if (
      facultyMembersCoordinated === "" ||
      Number(facultyMembersCoordinated) < 0
    ) {
      toast.warning(
        "Please enter a valid number of faculty members coordinated."
      );
      return;
    }

    if (!trimesterSession.trim()) {
      toast.warning("Please enter the trimester/session.");
      return;
    }

    const payload = {
      start_date: startDate,
      end_date: endDate,
      course_name: courseName.trim(),
      course_code: courseCode.trim(),
      sections_coordinated: Number(sectionsCoordinated),
      faculty_members_coordinated: Number(facultyMembersCoordinated),
      trimester_session: trimesterSession.trim(),
    };

    try {
      setLoading(true);

      if (editingId !== null) {
        await axios.put(
          `${BACKEND_URL}/api/course_coordination/${editingId}`,
          payload,
          authHeaders
        );

        toast.success("Course Coordination updated successfully!");
      } else {
        await axios.post(
          `${BACKEND_URL}/api/course_coordination`,
          payload,
          authHeaders
        );

        toast.success("Course Coordination saved successfully!");
      }

      clearForm();

      // Refresh saved records
      await refreshList();
    } catch (error) {
      console.error("Error saving Course Coordination:", error);

      toast.error(
        error.response?.data?.message ||
          (editingId !== null
            ? "Failed to update Course Coordination."
            : "Failed to save Course Coordination.")
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // Delete Course Coordination
  // =========================================================
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/course_coordination/${id}`,
        authHeaders
      );

      toast.success("Course Coordination deleted successfully!");

      setCourseCoordinationList((prev) =>
        prev.filter((item) => item.id !== id)
      );

      // If the deleted record was being edited, reset the form
      if (editingId === id) {
        clearForm();
      }
    } catch (error) {
      console.error("Error deleting Course Coordination:", error);

      toast.error(
        error.response?.data?.message ||
          "Failed to delete Course Coordination."
      );
    }
  };

  // =========================================================
  // Format date for display
  // =========================================================
  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);

    if (isNaN(d.getTime())) {
      return date;
    }

    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (fetching) {
    return <div style={styles.loading}>Loading...</div>;
  }

  const isEditing = editingId !== null;

  return (
    <div style={styles.page}>
      <ToastContainer position="top-center" autoClose={3000} />

      <div style={styles.card}>
        <h1 style={styles.title}>Course Coordination</h1>

        <p style={styles.description}>
          {isEditing
            ? "Editing the selected course coordination activity."
            : "Enter the details of the course coordination activity."}
        </p>

        {/* =================================================
            ADD / EDIT COURSE COORDINATION FORM
        ================================================== */}
        <form onSubmit={handleSubmit}>
          {/* Start Date */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Start Date</label>

            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={styles.input}
            />
          </div>

          {/* End Date */}
          <div style={styles.formGroup}>
            <label style={styles.label}>End Date</label>

            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={styles.input}
            />
          </div>

          {/* Course Name */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Course Name</label>

            <input
              type="text"
              value={courseName}
              onChange={(e) => setCourseName(e.target.value)}
              placeholder="Enter course name"
              style={styles.input}
            />
          </div>

          {/* Course Code */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Course Code</label>

            <input
              type="text"
              value={courseCode}
              onChange={(e) => setCourseCode(e.target.value)}
              placeholder="Enter course code"
              style={styles.input}
            />
          </div>

          {/* Number of Sections Coordinated */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Number of Sections Coordinated</label>

            <input
              type="number"
              min="0"
              value={sectionsCoordinated}
              onChange={(e) => setSectionsCoordinated(e.target.value)}
              placeholder="Enter number of sections"
              style={styles.input}
            />
          </div>

          {/* Number of Faculty Members Coordinated */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Number of Faculty Members Coordinated
            </label>

            <input
              type="number"
              min="0"
              value={facultyMembersCoordinated}
              onChange={(e) => setFacultyMembersCoordinated(e.target.value)}
              placeholder="Enter number of faculty members"
              style={styles.input}
            />
          </div>

          {/* Trimester / Session */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Trimester / Session</label>

            <input
              type="text"
              value={trimesterSession}
              onChange={(e) => setTrimesterSession(e.target.value)}
              placeholder="e.g. Spring 2026 / Summer 2026"
              style={styles.input}
            />
          </div>

          {/* Buttons */}
          <div style={styles.buttons}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={styles.backButton}
            >
              Back
            </button>

            <div style={styles.rightButtons}>
              {isEditing && (
                <button
                  type="button"
                  onClick={clearForm}
                  style={styles.cancelButton}
                >
                  Cancel Edit
                </button>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  ...styles.saveButton,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading
                  ? isEditing
                    ? "Updating..."
                    : "Saving..."
                  : isEditing
                  ? "Update"
                  : "Save"}
              </button>
            </div>
          </div>
        </form>

        {/* =================================================
            SAVED COURSE COORDINATION RECORDS
        ================================================== */}
        <div style={styles.savedSection}>
          <h2 style={styles.savedTitle}>Added Course Coordination</h2>

          {courseCoordinationList.length === 0 ? (
            <div style={styles.noData}>
              No course coordination has been added yet.
            </div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Start Date</th>
                    <th style={styles.th}>End Date</th>
                    <th style={styles.th}>Course Name</th>
                    <th style={styles.th}>Course Code</th>
                    <th style={styles.th}>Sections Coordinated</th>
                    <th style={styles.th}>Faculty Members Coordinated</th>
                    <th style={styles.th}>Trimester / Session</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {courseCoordinationList.map((item) => (
                    <tr
                      key={item.id}
                      style={
                        item.id === editingId ? styles.editingRow : undefined
                      }
                    >
                      <td style={styles.td}>{formatDate(item.start_date)}</td>

                      <td style={styles.td}>{formatDate(item.end_date)}</td>

                      <td style={styles.td}>{item.course_name}</td>

                      <td style={styles.td}>{item.course_code}</td>

                      <td style={styles.td}>{item.sections_coordinated}</td>

                      <td style={styles.td}>
                        {item.faculty_members_coordinated}
                      </td>

                      <td style={styles.td}>{item.trimester_session}</td>

                      <td style={styles.td}>
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          style={styles.editButton}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          style={styles.deleteButton}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f4f6fb",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "30px",
    boxSizing: "border-box",
  },

  card: {
    width: "100%",
    maxWidth: "1200px",
    backgroundColor: "white",
    padding: "35px",
    borderRadius: "12px",
    boxShadow: "0 5px 20px rgba(0,0,0,0.1)",
    boxSizing: "border-box",
  },

  title: {
    color: "#1e3a8a",
    textAlign: "center",
    marginBottom: "10px",
  },

  description: {
    textAlign: "center",
    color: "#666",
    marginBottom: "30px",
  },

  formGroup: {
    marginBottom: "25px",
  },

  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "bold",
    color: "#333",
  },

  input: {
    width: "100%",
    padding: "12px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    fontSize: "16px",
    boxSizing: "border-box",
    outline: "none",
  },

  buttons: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "25px",
  },

  rightButtons: {
    display: "flex",
    gap: "10px",
  },

  backButton: {
    padding: "10px 20px",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#777",
    color: "white",
    cursor: "pointer",
  },

  cancelButton: {
    padding: "10px 20px",
    border: "1px solid #3b82f6",
    borderRadius: "6px",
    backgroundColor: "white",
    color: "#3b82f6",
    cursor: "pointer",
    fontWeight: "bold",
  },

  saveButton: {
    padding: "10px 25px",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#3b82f6",
    color: "white",
    cursor: "pointer",
    fontWeight: "bold",
  },

  savedSection: {
    marginTop: "40px",
  },

  savedTitle: {
    color: "#1e3a8a",
    marginBottom: "15px",
  },

  noData: {
    padding: "20px",
    textAlign: "center",
    color: "#777",
    backgroundColor: "#f8fafc",
    border: "1px solid #ddd",
    borderRadius: "6px",
  },

  tableWrapper: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    border: "1px solid #ddd",
  },

  th: {
    backgroundColor: "#1e3a8a",
    color: "white",
    padding: "12px",
    textAlign: "left",
    border: "1px solid #ddd",
    whiteSpace: "nowrap",
  },

  td: {
    padding: "12px",
    border: "1px solid #ddd",
    color: "#333",
    whiteSpace: "nowrap",
  },

  editingRow: {
    backgroundColor: "#dbeafe",
  },

  editButton: {
    padding: "7px 12px",
    border: "none",
    borderRadius: "5px",
    backgroundColor: "#2563eb",
    color: "white",
    cursor: "pointer",
    marginRight: "8px",
  },

  deleteButton: {
    padding: "7px 12px",
    border: "none",
    borderRadius: "5px",
    backgroundColor: "#dc2626",
    color: "white",
    cursor: "pointer",
  },

  loading: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "20px",
  },
};

export default CourseCoordination;